import {Component, computed, inject, Inject, LOCALE_ID, makeStateKey, OnInit, PendingTasks, PLATFORM_ID, signal, TransferState} from '@angular/core';
import {isPlatformServer} from '@angular/common';
import {RouterLink} from '@angular/router';
import {ProductReviewsComponent} from './product-reviews.component';
import {AuthedUserService} from './services/authed-user.service';
import {StatsService, PublicStats, fetchPublicStats} from './services/stats.service';
import {SeoService} from './services/seo.service';
import {allPages} from './pages/page';
import {pathOf} from './content/content.model';
import {ContentPageRef, pagesFor} from './content/content-registry';
import {CityStats, fetchCities} from './cities/city-data';
import {CityChipCountComponent} from './cities/city-counts.component';
import {commercialLinkFor} from './content/commercial-targets';
import {RevealDirective} from './content/reveal.directive';
import {trackAppClick} from './services/analytics';
import {CountUpComponent} from './home/count-up.component';
import {HomeHeroComponent} from './home/home-hero.component';
import {exactParts, roundedParts} from './home/stat-parts';
import {FeaturedEvent, fetchFeaturedEvents} from './home/featured-event';
import {EventListComponent} from './home/event-list.component';

interface Way {
    tone: 'sun' | 'rose' | 'mint';
    title: string;
    body: string;
    image: string;
    alt: string;
    link: string;
}

/** The server's pick of the featured events, handed to the browser so hydration keeps the list. */
const FEATURED_EVENTS_KEY = makeStateKey<FeaturedEvent[]>('home-featured-events');

/** The figures baked at prerender, handed to the browser so hydration keeps them. */
const PUBLIC_STATS_KEY = makeStateKey<PublicStats | null>('home-public-stats');

@Component({
    template: `
        <cb-home-hero [stats]="stats()" [organizerLink]="organizerLink" (join)="openApp('landing-hero')"/>

        <!-- ======================= AROUND THE TABLE ======================= -->
        @if (events().length) {
            <section class="night events-band">
                <div class="wrap">
                    <div class="band-head">
                        <div>
                            <p class="kicker" i18n="@@home-events-kicker">Coming up on Ludoya</p>
                            <h2 class="h2" i18n="@@home-events-title">Around the table</h2>
                            <p class="lede on-dark" i18n="@@home-events-lede">Some of the game nights and conventions coming up.</p>
                        </div>
                        <a class="more-link on-dark" [href]="eventsUrl" target="_blank" rel="noopener" (click)="trackMoreEvents()">
                            <span i18n="@@home-events-more">See more events</span>
                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </a>
                    </div>
                    <cb-event-list [events]="events()"/>
                </div>
            </section>
        }

        <!-- ============================ STATS ============================
             Figures baked at prerender, so crawlers and link previews read real numbers, then
             refreshed in the browser. It leaves only if neither call got an answer. -->
        @if (!statsUnavailable()) {
            <section class="cream stats-band">
                <div class="wrap">
                    <h2 class="h2 stats-title" i18n="@@home-stats-title">Already on Ludoya</h2>
                    <div class="stats">
                        @for (stat of statItems(); track stat.label; let i = $index) {
                            <div class="stat" [class]="'stat tone-' + (i % 3)">
                                <span class="stat-figure">
                                    @if (stat.parts; as p) {
                                        <cb-count-up [value]="p.n" [decimals]="p.decimals" [suffix]="p.suffix" [delay]="i * 90"/>
                                    }
                                </span>
                                <span class="stat-label">{{ stat.label }}</span>
                            </div>
                        }
                    </div>
                </div>
            </section>
        }

        <!-- ========================= THREE WAYS IN ========================= -->
        <section class="cream ways-band" cbReveal>
            <div class="wrap">
                <div class="ways-head">
                    <h2 class="h2" i18n="@@home-ways-title">What you can do with Ludoya</h2>
                    <p class="lede" i18n="@@home-ways-lede">Plan game nights, log every play and keep your collection, in one free app.</p>
                </div>
                <ol class="ways">
                    @for (way of ways; track way.title; let i = $index) {
                        <li class="way" [class]="'way tone-' + way.tone">
                            <div class="way-title-row">
                                <span class="way-number" aria-hidden="true">{{ i + 1 }}</span>
                                <h3 class="way-title">{{ way.title }}</h3>
                            </div>
                            <p class="way-body">{{ way.body }}</p>
                            <a class="way-visual" [routerLink]="way.link">
                                <svg class="rays" viewBox="0 0 60 60" width="54" height="54" aria-hidden="true">
                                    <path d="M30 22 L 30 4 M 18 26 L 6 14 M 42 26 L 54 14" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round"/>
                                </svg>
                                <img [src]="way.image" width="840" height="1400" [alt]="way.alt" loading="lazy" decoding="async"/>
                            </a>
                        </li>
                    }
                </ol>
                <a class="more-link all-guides" routerLink="/learn">
                    <span i18n="@@browse-all-features">Browse all {{ totalFeaturePages }} feature guides</span>
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </a>
            </div>
        </section>

        <!-- ======================= EVERY GAME HAS A STORY ======================= -->
        <section class="sky story-band" cbReveal>
            <div class="wrap story-grid">
                <div class="story-copy">
                    <h2 class="story-title" i18n="@@home-story-title">Every game has a story. <mark class="marker">Keep yours here.</mark></h2>
                    <p class="lede" i18n="@@home-story-lede">Every play you log adds up: stats, badges, rivalries, and a monthly Replay of your year at the table that you'll want to share.</p>
                    <button class="cb-cta" type="button" (click)="openApp('landing-story')">
                        <span i18n="@@start-free">Start free</span>
                        <span class="cb-cta-chevron" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                    </button>
                    <p class="bgg-line">
                        <span i18n="@@home-bgg-line">Coming from BoardGameGeek? Your collection and every play you logged come with you, BG Stats plays included once they are on BGG.</span>
                        <a class="more-link" [routerLink]="bggLink">
                            <span i18n="@@home-bgg-link">How the sync works</span>
                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </a>
                    </p>
                </div>
                <div class="story-visual">
                    <div class="laptop">
                        <div class="laptop-screen">
                            <img src="/assets/screenshots/home-replay-biggest-day.webp" width="2560" height="1720" loading="lazy" decoding="async"
                                 alt="A monthly Replay card: the biggest day of the month, with its plays, hours at the table and opponents" i18n-alt="@@home-story-alt"/>
                        </div>
                        <div class="laptop-base" aria-hidden="true"></div>
                    </div>
                </div>
            </div>
        </section>

        <!-- ======================= WHO IT IS FOR ======================= -->
        @if (solutionPages.length) {
            <section class="white audiences-band" cbReveal>
                <div class="wrap">
                    <h2 class="h2" i18n="@@home-audiences-title">Who it's for</h2>
                    <ul class="audiences">
                        @for (page of solutionPages; track page.slug; let i = $index) {
                            <li>
                                <a class="audience" [routerLink]="pathFor(page)">
                                    <span class="audience-icon" [class]="'audience-icon tone-' + (i % 4)" aria-hidden="true">
                                        <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
                                            @switch (page.slug) {
                                                @case ('players') {
                                                    <circle cx="12" cy="8" r="3.6"/><path d="M5 20c.8-3.8 3.6-5.8 7-5.8s6.2 2 7 5.8"/>
                                                }
                                                @case ('clubs') {
                                                    <circle cx="9" cy="8.5" r="3"/><circle cx="16.5" cy="9.5" r="2.4"/><path d="M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5M14.5 14.3c.7-.2 1.3-.3 2-.3 2.2 0 3.9 1.4 4.4 4"/>
                                                }
                                                @case ('venues') {
                                                    <path d="M4 10v10h16V10M3 10l1.6-5.5h14.8L21 10z"/><path d="M3 10c0 1.4 1.2 2.3 2.5 2.3S8 11.4 8 10c0 1.4 1.2 2.3 2.5 2.3S13 11.4 13 10c0 1.4 1.2 2.3 2.5 2.3S18 11.4 18 10c0 1.4 1.2 2.3 1.5 2.3"/><path d="M10 20v-5h4v5"/>
                                                }
                                                @case ('festivals') {
                                                    <path d="M3 20h18M5 20 12 5l7 15M12 5V2.5l3 1.2-3 1.2M9.5 20l2.5-5 2.5 5"/>
                                                }
                                                @case ('publishers') {
                                                    <path d="M3.5 7.5 12 3.5l8.5 4v9L12 20.5l-8.5-4z"/><path d="M3.5 7.5 12 11.5l8.5-4M12 11.5v9"/>
                                                }
                                                @case ('creators') {
                                                    <rect x="3" y="6.5" width="12.5" height="11" rx="2.5"/><path d="m15.5 10.5 5.5-3v9l-5.5-3"/>
                                                }
                                                @case ('partnerships') {
                                                    <path d="M4 10.5v3.5a1.5 1.5 0 0 0 1.5 1.5H7l5 4V5L7 9H5.5A1.5 1.5 0 0 0 4 10.5zM16 8.5a5 5 0 0 1 0 7M18.8 6a8.5 8.5 0 0 1 0 12"/>
                                                }
                                                @default {
                                                    <circle cx="12" cy="12" r="7"/>
                                                }
                                            }
                                        </svg>
                                    </span>
                                    <span class="audience-name">{{ page.navLabel }}</span>
                                    <span class="audience-teaser">{{ page.teaser }}</span>
                                    <svg class="audience-go" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                                </a>
                            </li>
                        }
                    </ul>
                </div>
            </section>
        }

        <!-- Testimonials -->
        <section class="cream reviews-band" cbReveal>
            <app-product-reviews></app-product-reviews>
        </section>

        <!-- City directory teaser — real places, straight from the same endpoint as /board-games -->
        @if (cities().length) {
            <section class="white cities-band" cbReveal>
                <div class="wrap">
                    <div class="cities-head">
                        <h2 class="h2" i18n="@@home-cities-heading">Already happening in {{ cities().length }} cities</h2>
                        <p class="lede" i18n="@@home-cities-sub-plain">Clubs, game nights and players, city by city.</p>
                    </div>
                    <div class="cities">
                        @for (city of cities().slice(0, 12); track city.slug) {
                            <a class="city" [routerLink]="['/board-games', city.slug]">
                                <span class="city-name">{{ city.name }}</span>
                                <span class="city-count">
                                    <app-city-chip-count [communities]="city.communities" [events]="city.events"/>
                                </span>
                            </a>
                        }
                        <a class="city all-cities" routerLink="/board-games">
                            <span class="city-name" i18n="@@home-cities-all">All cities</span>
                            <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
                        </a>
                    </div>
                </div>
            </section>
        }

        <!-- ============================ CLOSE ============================ -->
        <section class="night close-band">
            <div class="wrap close-inner">
                <h2 class="close-title" i18n="@@play-more-play-together">Play more. Play together.</h2>
                <p class="lede on-dark" i18n="@@game-page-ludoya-eyebrow">Free, on web, Android and iOS</p>
                <button class="cb-cta" type="button" (click)="openApp('landing-bottom')">
                    <span i18n="@@start-free">Start free</span>
                    <span class="cb-cta-chevron" aria-hidden="true"><svg viewBox="0 0 24 24" width="18" height="18"><path d="M5 12h13M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/></svg></span>
                </button>
            </div>
        </section>
    `,
    styles: [`
        :host {
            display: block;
            font-family: var(--h-body);
            color: var(--h-ink);
        }

        .wrap {
            max-width: 1168px;
            margin: 0 auto;
            padding: 0 24px;
        }

        .night { background: var(--h-night); color: white; }
        .cream { background: var(--h-cream); }
        .sky { background: var(--h-sky); }
        .white { background: white; }

        section {
            position: relative;
        }

        .h2 {
            margin: 0;
            font-family: var(--h-display);
            font-size: clamp(2rem, 1.3rem + 2.2vw, 3.1rem);
            font-weight: 800;
            line-height: 1.08;
            letter-spacing: -0.03em;
        }

        .lede {
            margin: 14px 0 0;
            font-size: 1.08rem;
            line-height: 1.6;
            color: var(--h-muted);
            max-width: 38em;
        }

        .lede.on-dark {
            color: rgba(255, 255, 255, 0.7);
        }

        .kicker {
            margin: 0 0 10px;
            font-size: 0.95rem;
            font-weight: 700;
            color: var(--h-rose);
        }

        .more-link {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            font-weight: 700;
            color: var(--cb-teal);
            text-decoration: none;
        }

        .more-link.on-dark {
            color: var(--h-mint);
        }

        .more-link svg {
            transition: transform 0.2s ease;
        }

        .more-link:hover svg {
            transform: translateX(3px);
        }

        .more-link:hover span {
            text-decoration: underline;
            text-underline-offset: 4px;
        }

        /* ========================= AROUND THE TABLE ========================= */
        .events-band {
            padding: 24px 0 104px;
        }

        .band-head {
            display: flex;
            flex-wrap: wrap;
            align-items: flex-end;
            justify-content: space-between;
            gap: 20px 40px;
            margin-bottom: 40px;
        }

        /* ============================ STATS ============================ */
        .stats-band {
            padding: 96px 0 40px;
        }

        .stats-title {
            max-width: 16em;
            margin-bottom: 48px;
        }

        .stats {
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 40px;
        }

        .stat {
            display: flex;
            flex-direction: column;
            gap: 6px;
            padding-top: 18px;
            border-top: 4px solid var(--tone);
        }

        .tone-0 { --tone: var(--h-sun); }
        .tone-1 { --tone: var(--h-rose); }
        .tone-2 { --tone: var(--h-mint); }

        .stat-figure {
            font-family: var(--h-display);
            font-size: clamp(2.6rem, 1.8rem + 2.4vw, 3.8rem);
            font-weight: 800;
            line-height: 1;
            letter-spacing: -0.04em;
            /* Holds the line open while the figure is still on its way. */
            min-height: 1em;
        }

        .stat-label {
            font-size: 1rem;
            color: var(--h-muted);
        }

        /* ========================= THREE WAYS IN ========================= */
        .ways-band {
            padding: 96px 0 112px;
        }

        .ways-head {
            display: grid;
            grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr);
            gap: 24px 64px;
            align-items: end;
            margin-bottom: 56px;
        }

        .ways-head .lede {
            margin: 0;
        }

        .ways {
            list-style: none;
            margin: 0;
            padding: 0;
            display: grid;
            grid-template-columns: repeat(3, minmax(0, 1fr));
            gap: 40px;
        }

        .way {
            display: flex;
            flex-direction: column;
        }

        .way.tone-sun { --tone: var(--h-sun); --tint: #FFEFC7; }
        .way.tone-rose { --tone: var(--h-rose); --tint: #FCDDE8; }

        .way.tone-rose .way-number {
            color: white;
        }
        .way.tone-mint { --tone: var(--h-mint); --tint: #D2F7EF; }

        .way-title-row {
            display: flex;
            align-items: center;
            gap: 14px;
        }

        .way-number {
            flex: 0 0 auto;
            display: grid;
            place-items: center;
            width: 44px;
            height: 44px;
            border-radius: 50%;
            background: var(--tone);
            font-family: var(--h-display);
            font-size: 1.25rem;
            font-weight: 800;
            color: var(--h-ink);
        }

        .way-title {
            margin: 0;
            font-family: var(--h-display);
            font-size: 1.45rem;
            font-weight: 800;
            letter-spacing: -0.02em;
        }

        .way-body {
            margin: 12px 0 26px;
            font-size: 0.98rem;
            line-height: 1.6;
            color: var(--h-muted);
        }

        /* The real screen rising out of a tinted card, with three hand-drawn rays on its corner. */
        .way-visual {
            position: relative;
            display: block;
            margin-top: auto;
            height: 300px;
            padding: 36px 44px 0;
            border-radius: 36px 36px 36px 12px;
            background: var(--tint);
            overflow: hidden;
            transition: transform 0.25s ease;
        }

        .way-visual:hover {
            transform: translateY(-4px);
        }

        .way-visual:focus-visible {
            outline: 3px solid var(--h-ink);
            outline-offset: 3px;
        }

        .way-visual img {
            display: block;
            width: 100%;
            height: auto;
            border-radius: 24px 24px 0 0;
            box-shadow: 0 -2px 0 6px #16222C, 0 20px 40px -10px rgba(19, 26, 34, 0.45);
            background: #0E1B1A;
        }

        .rays {
            position: absolute;
            top: 6px;
            right: 12px;
            color: var(--tone);
            transform: rotate(18deg);
        }

        .all-guides {
            margin-top: 40px;
        }

        /* ======================= EVERY GAME HAS A STORY ======================= */
        .story-band {
            padding: 112px 0;
            overflow: hidden;
        }

        .story-grid {
            display: grid;
            grid-template-columns: minmax(0, 1fr) minmax(0, 1.15fr);
            gap: 64px;
            align-items: center;
        }

        .story-title {
            margin: 0;
            font-family: var(--h-display);
            font-size: clamp(2.3rem, 1.4rem + 3vw, 3.9rem);
            font-weight: 800;
            line-height: 1.08;
            letter-spacing: -0.04em;
        }

        /* A highlighter pass: slightly ragged, slightly tilted, behind the words rather than on them. */
        .marker {
            color: inherit;
            background: none;
            padding: 0 0.06em;
            background-image: linear-gradient(100deg, rgba(255, 176, 16, 0) 0.5%, #FFC23F 2%, #FFD166 96%, rgba(255, 176, 16, 0) 99%);
            background-size: 100% 0.62em;
            background-position: 0 88%;
            background-repeat: no-repeat;
            -webkit-box-decoration-break: clone;
            box-decoration-break: clone;
        }

        @media (min-width: 600px) {
            .marker {
                white-space: nowrap;
            }
        }

        .story-copy .lede {
            margin-bottom: 32px;
        }

        .bgg-line {
            display: flex;
            flex-wrap: wrap;
            align-items: baseline;
            gap: 6px 14px;
            margin: 36px 0 0;
            padding-top: 22px;
            border-top: 1px solid rgba(19, 26, 34, 0.12);
            font-size: 0.98rem;
            line-height: 1.55;
            color: var(--h-ink);
        }

        .story-visual {
            position: relative;
        }

        .laptop-screen {
            padding: 12px 12px 0;
            border-radius: 20px 20px 0 0;
            background: #11181F;
            box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.06);
        }

        .laptop-screen img {
            display: block;
            width: 100%;
            height: auto;
            border-radius: 8px 8px 0 0;
        }

        .laptop-base {
            position: relative;
            height: 16px;
            margin: 0 -7%;
            border-radius: 0 0 22px 22px;
            background: linear-gradient(180deg, #D5DBE1 0%, #A9B2BB 100%);
            box-shadow: 0 30px 50px -20px rgba(19, 26, 34, 0.5);
        }

        .laptop-base::before {
            content: '';
            position: absolute;
            left: 50%;
            top: 0;
            width: 16%;
            height: 6px;
            transform: translateX(-50%);
            border-radius: 0 0 8px 8px;
            background: #8F99A3;
        }



        /* ======================= WHO IT IS FOR ======================= */
        .audiences-band {
            padding: 104px 0;
        }

        .audiences {
            list-style: none;
            margin: 40px 0 0;
            padding: 0;
            border-top: 1px solid var(--h-line);
        }

        .audiences li {
            border-bottom: 1px solid var(--h-line);
        }

        .audience {
            display: grid;
            grid-template-columns: 52px minmax(0, 260px) minmax(0, 1fr) 24px;
            gap: 24px;
            align-items: center;
            padding: 22px 16px;
            margin: 0 -16px;
            color: var(--h-ink);
            text-decoration: none;
            transition: background 0.2s ease;
        }

        .audience:hover {
            background: var(--h-cream);
        }

        .audience:focus-visible {
            outline: 3px solid var(--h-rose);
            outline-offset: -3px;
        }

        /* A drawn icon on a soft tile of the row's colour: amber, raspberry, mint, sky in turn. */
        .audience-icon {
            display: grid;
            place-items: center;
            width: 52px;
            height: 52px;
            border-radius: 16px;
            background: var(--tint);
            color: var(--ink-tone);
        }

        .audience-icon.tone-0 { --tint: #FFEFC7; --ink-tone: #946000; }
        .audience-icon.tone-1 { --tint: #FCDDE8; --ink-tone: var(--h-rose-deep); }
        .audience-icon.tone-2 { --tint: #D2F7EF; --ink-tone: #0B7F69; }
        .audience-icon.tone-3 { --tint: #DDEEFA; --ink-tone: #2B6CA3; }

        .audience-name {
            font-family: var(--h-display);
            font-size: 1.35rem;
            font-weight: 800;
            letter-spacing: -0.02em;
        }

        .audience-teaser {
            font-size: 0.98rem;
            line-height: 1.6;
            color: var(--h-muted);
            max-width: 62ch;
        }

        .audience-go {
            color: var(--cb-teal);
            transition: transform 0.2s ease;
        }

        .audience:hover .audience-go {
            transform: translateX(4px);
        }

        /* ============================ CITIES ============================ */
        .cities-band {
            padding: 104px 0;
        }

        .cities-head {
            text-align: center;
            margin: 0 auto 40px;
            max-width: 720px;
        }

        .cities-head .lede {
            margin-left: auto;
            margin-right: auto;
        }

        .cities {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 10px;
        }

        .city {
            display: inline-flex;
            align-items: baseline;
            gap: 8px;
            padding: 11px 18px;
            border-radius: 999px;
            border: 1px solid var(--h-line);
            background: var(--h-cream);
            text-decoration: none;
            transition: border-color 0.15s ease, background 0.15s ease;
        }

        .city:hover {
            border-color: var(--h-rose);
            background: white;
        }

        .city-name { font-weight: 700; color: var(--h-ink); }
        .city-count { font-size: 0.8rem; color: var(--h-muted); }
        .all-cities { align-items: center; background: var(--h-ink); border-color: var(--h-ink); color: white; }
        .all-cities .city-name { color: white; }
        .all-cities:hover { background: var(--h-night-2); border-color: var(--h-night-2); }

        /* ============================ CLOSE ============================ */
        .close-band {
            overflow: hidden;
            padding: 112px 0 120px;
            text-align: center;
        }

        .close-inner {
            display: flex;
            flex-direction: column;
            align-items: center;
        }


        .close-title {
            margin: 0;
            font-family: var(--h-display);
            font-size: clamp(2.6rem, 1.4rem + 4vw, 5rem);
            font-weight: 800;
            line-height: 1;
            letter-spacing: -0.04em;
        }

        .close-band .lede {
            margin: 18px auto 36px;
        }

        /* ============================ RESPONSIVE ============================ */
        @media (max-width: 960px) {
            .story-grid,
            .ways-head {
                grid-template-columns: minmax(0, 1fr);
            }

            .ways {
                grid-template-columns: minmax(0, 1fr);
                max-width: 520px;
                margin: 0 auto;
            }

            .laptop-base {
                margin: 0 -3%;
            }


            .stats {
                grid-template-columns: repeat(2, minmax(0, 1fr));
                gap: 32px 24px;
            }

            .audience {
                grid-template-columns: 44px minmax(0, 1fr) 20px;
                gap: 6px 14px;
            }

            .audience-icon {
                grid-row: 1 / span 2;
                width: 44px;
                height: 44px;
                border-radius: 14px;
            }

            .audience-teaser {
                grid-column: 2;
                grid-row: 2;
            }

            .audience-go {
                grid-column: 3;
                grid-row: 1 / span 2;
            }
        }

        @media (max-width: 600px) {
            .wrap {
                padding: 0 16px;
            }

            .events-band,
            .ways-band,
            .audiences-band,
            .cities-band {
                padding-top: 72px;
                padding-bottom: 72px;
            }

            .events-band {
                padding-top: 16px;
            }

            .stats-band {
                padding-top: 72px;
            }

            .story-band {
                padding: 72px 0;
            }

            .close-band {
                padding: 104px 0 96px;
            }

            .way-visual {
                height: 260px;
                padding: 28px 32px 0;
            }

            .cities-head {
                text-align: left;
            }

            .cities {
                justify-content: flex-start;
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .way-visual, .audience, .audience-go, .more-link svg { transition: none; }
        }
    `],
    imports: [
        ProductReviewsComponent, RouterLink, RevealDirective, CityChipCountComponent, CountUpComponent,
        EventListComponent, HomeHeroComponent,
    ],
})
export class LandingComponent implements OnInit {

    readonly stats = signal<PublicStats | null>(null);
    /** Set only when no figures came through at all: the band then folds away instead of staying blank. */
    readonly statsUnavailable = signal(false);
    readonly cities = signal<CityStats[]>([]);
    readonly events = signal<FeaturedEvent[]>([]);

    /** The six figures in display order, with their parts null until the API has answered. */
    readonly statItems = computed(() => {
        const s = this.stats();
        return [
            {label: $localize`:@@stat-label-players:Board gamers on Ludoya`, parts: s && roundedParts(s.players)},
            {label: $localize`:@@stat-label-active-this-month:Playing this month`, parts: s && exactParts(s.activeThisMonth)},
            {label: $localize`:@@stat-label-active-today:Here today`, parts: s && exactParts(s.activeToday)},
            {label: $localize`:@@stat-label-communities:Clubs, shops and communities`, parts: s && roundedParts(s.communities)},
            {label: $localize`:@@plays-logged-and-counting:Plays logged and counting`, parts: s && roundedParts(s.playsLogged)},
            {label: $localize`:@@stat-label-events:Game nights and events`, parts: s && roundedParts(s.eventsHosted)},
        ];
    });

    /** Audience pages authored in this build's locale; the section hides when there are none. */
    readonly solutionPages: ContentPageRef[];

    readonly ways: Way[];

    readonly totalFeaturePages: number;

    /** The BGG sync page in locales that have one, else the integrations guide. */
    readonly bggLink: string;

    readonly eventsUrl: string;

    /** The clubs page in locales that have one, else the organizers' guide. */
    readonly organizerLink: string;

    private readonly transferState = inject(TransferState);

    constructor(
        private authedUserService: AuthedUserService,
        private statsService: StatsService,
        private seoService: SeoService,
        @Inject(LOCALE_ID) private pageLocale: string,
        @Inject(PLATFORM_ID) private platformId: object,
        private pendingTasks: PendingTasks,
    ) {
        this.solutionPages = pagesFor('solution', this.pageLocale);

        // The hold makes SSG wait for the list, so crawlers see the band with real cities.
        const doneCities = this.pendingTasks.add();
        fetchCities().then(cities => this.cities.set(cities), () => undefined).finally(doneCities);

        // The featured events are baked into the page for crawlers and the first paint, handed to
        // hydration so the list does not blink out, then refetched in the browser: a page baked
        // days ago must never keep listing an event that has already started.
        if (isPlatformServer(this.platformId)) {
            const doneEvents = this.pendingTasks.add();
            fetchFeaturedEvents()
                .then(events => {
                    this.events.set(events ?? []);
                    this.transferState.set(FEATURED_EVENTS_KEY, events ?? []);
                })
                .finally(doneEvents);
        } else {
            this.events.set(this.transferState.get(FEATURED_EVENTS_KEY, []));
            // One try in the browser: a failed refetch keeps what the page was served with.
            fetchFeaturedEvents([]).then(events => {
                if (events) {
                    this.events.set(events);
                }
            });
        }

        const link = (slug: string) => commercialLinkFor(slug, this.pageLocale) ?? `/learn/${slug}`;
        this.ways = [
            {
                tone: 'sun',
                title: $localize`:@@home-way-find-title:Find a table`,
                body: $localize`:@@home-way-find-body:Find game nights near you and RSVP in a tap. Or host your own, with seats, waitlists and reminders handled for you.`,
                image: '/assets/screenshots/home-gallery-event.webp',
                alt: $localize`:@@home-gallery-alt-event:A convention weekend's schedule: day tabs and time slots, tables filling up and wait-listing`,
                link: link('events'),
            },
            {
                tone: 'rose',
                title: $localize`:@@home-way-log-title:Log the moment`,
                body: $localize`:@@home-way-log-body:Log a play in seconds: players, scores, photos and place. Your stats, badges and yearly Replay fill in by themselves.`,
                image: '/assets/screenshots/home-gallery-play.webp',
                alt: $localize`:@@home-gallery-alt-play:A logged play with a photo of the table, players and scores`,
                link: link('play-logging'),
            },
            {
                tone: 'mint',
                title: $localize`:@@home-way-shelf-title:Keep your shelf close`,
                body: $localize`:@@home-way-shelf-body:Catalog what you own, want or sold. It syncs both ways with BoardGameGeek, tracks loans and filters any way you like.`,
                image: '/assets/screenshots/home-gallery-collection.webp',
                alt: $localize`:@@home-gallery-alt-collection:A game collection in a colorful grid, filters and expansions included`,
                link: link('collection'),
            },
        ];
        this.totalFeaturePages = allPages.filter(p => p.uri.startsWith('learn/')).length;
        this.bggLink = commercialLinkFor('integrations', this.pageLocale) ?? '/learn/integrations';
        this.eventsUrl = this.authedUserService.getAppUrl('/explore/events');
        const clubs = this.solutionPages.find(p => p.slug === 'clubs');
        this.organizerLink = clubs ? pathOf(clubs) : '/learn/guide-clubs-organizers';
    }

    openApp(from: string): void {
        trackAppClick(from);
        window.open(this.authedUserService.getAppUrl(), '_blank', 'noopener,noreferrer');
    }

    trackMoreEvents(): void {
        trackAppClick('landing-events-more', '/explore/events');
    }

    pathFor(page: ContentPageRef): string {
        return pathOf(page);
    }

    ngOnInit() {
        this.seoService.update({
            title: $localize`:@@seo-home-title:Board Game Plays, Groups & Events`,
            description: $localize`:@@seo-home-description:Log your board game plays, build your collection, and find local groups and events. Ludoya: share every play, find your people.`,
            path: '/',
        });
        this.seoService.setJsonLd([
            {'@context': 'https://schema.org', ...this.seoService.organizationSchema()},
            {
                '@context': 'https://schema.org',
                '@type': 'WebSite',
                name: 'Ludoya',
                url: this.seoService.canonical('/'),
            },
            {
                '@context': 'https://schema.org',
                '@type': 'SoftwareApplication',
                name: 'Ludoya',
                applicationCategory: 'LifestyleApplication',
                operatingSystem: 'Android, iOS, Web',
                offers: {'@type': 'Offer', price: '0', priceCurrency: 'USD'},
            },
        ]);

        // Baked at prerender so crawlers and link previews see the figures, then refetched in the
        // browser: the cumulative ones are rounded and barely move, but "Here today" from a page
        // baked days ago must not stay on screen. A failed refetch keeps what the page shipped with.
        if (isPlatformServer(this.platformId)) {
            const doneStats = this.pendingTasks.add();
            fetchPublicStats()
                .then(stats => {
                    this.stats.set(stats);
                    this.transferState.set(PUBLIC_STATS_KEY, stats);
                })
                .finally(doneStats);
        } else {
            this.stats.set(this.transferState.get(PUBLIC_STATS_KEY, null));
            this.statsService.getPublicStats().subscribe(stats => {
                if (stats) {
                    this.stats.set(stats);
                } else if (!this.stats()) {
                    this.statsUnavailable.set(true);
                }
            });
        }
    }
}

